import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { Observable, Subject, of } from 'rxjs';
import { finalize, map, startWith, takeUntil } from 'rxjs/operators';

import { OrganizationDto } from '../../../../../modules/organizations/dtos/organization';
import { OrganizationsService } from '../../../../../modules/organizations/organizations.service';
import { PaginationArgs } from '../../../../../shared/components/pagination/models/pagination-args.model';
import { SnackBarService } from '../../../../../shared/components/snack-bar/snack-bar.service';
import { SortingType } from '../../../../../shared/enums/sorting-type.enum';
import { PartnerRegistration } from '../../../models/organizations.model';
import { OrganizationsLegacyService } from '../../../organizations-legacy.service';

interface DialogData {
	mode: 'link' | 'unlink';
	partner: PartnerRegistration;
}

type OrgControlValue = OrganizationDto | string | null;

@Component({
	selector: 'app-link-organizations-modal',
	styleUrls: ['./link-organizations-modal.component.scss'],
	templateUrl: './link-organizations-modal.component.html',
})
export class LinkOrganizationsModalComponent implements OnInit, OnDestroy {
	availableOrganizations$: Observable<OrganizationDto[]> = of([]);
	displayOrg = (org: OrganizationDto | null): string =>
		org?.name_secondary ?? '';

	filteredOrganizations$: Observable<OrganizationDto[]> = of([]);

	isLoading = true;

	organizationControl = new FormControl<OrgControlValue>(null, [
		Validators.required,
	]);

	private readonly destroy$ = new Subject<void>();

	private orgCache: OrganizationDto[] = [];

	private selectedOrgId: string | null = null;

	constructor(
		public dialogRef: MatDialogRef<LinkOrganizationsModalComponent>,
		@Inject(MAT_DIALOG_DATA) public data: DialogData,
		private readonly organizationsService: OrganizationsService,
		private readonly legacyOrgService: OrganizationsLegacyService,
		private readonly snackBarService: SnackBarService,
	) {}

	closeModal(): void {
		this.dialogRef.close();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngOnInit(): void {
		this.isLoading = true;

		const select = JSON.stringify({
			id: true,
			name_secondary: true,
		});

		const orderBy = JSON.stringify({ name_secondary: SortingType.ASCENDING });

		if (this.data.mode === 'link') {
			const whereClause = {
				is_matriz: true,
				organizationBrokerLink: null,
			};

			this.organizationsService
				.findMany(
					new PaginationArgs({
						limit: 150,
						orderBy,
						page: 1,
						select,
						where: whereClause,
					}),
				)
				.pipe(
					map((response) => response.result),
					finalize(() => (this.isLoading = false)),
					takeUntil(this.destroy$),
				)
				.subscribe({
					error: () => {
						this.orgCache = [];
						this.availableOrganizations$ = of([]);
						this.setupFilterStream();
					},
					next: (list) => {
						this.orgCache = list ?? [];
						this.availableOrganizations$ = of(this.orgCache);
						this.setupFilterStream();
					},
				});
		} else {
			const whereClause = {
				is_matriz: true,
				organizationBrokerLink: {
					deletedAt: null,
					partnerBrokerId: this.data.partner.id,
				},
			};

			this.legacyOrgService
				.findOrganizationsByFilter(whereClause)
				.pipe(
					finalize(() => (this.isLoading = false)),
					takeUntil(this.destroy$),
				)
				.subscribe({
					error: () => {
						this.orgCache = [];
						this.availableOrganizations$ = of([]);
						this.setupFilterStream();
					},
					next: (list) => {
						this.orgCache = (list ?? []) as OrganizationDto[];
						this.availableOrganizations$ = of(this.orgCache);
						this.setupFilterStream();
					},
				});
		}
	}

	onOrgPicked(org: OrganizationDto): void {
		this.selectedOrgId = org?.id != null ? String(org.id) : null;
	}

	onOrgSelected(event: MatAutocompleteSelectedEvent): void {
		const org = event.option.value as OrganizationDto;
		this.selectedOrgId = org?.id != null ? String(org.id) : null;
	}

	saveItems(): void {
		if (!this.selectedOrgId) {
			this.organizationControl.setErrors({ required: true });
			this.organizationControl.markAsTouched();

			return;
		}

		const selectedOrgId = this.selectedOrgId;
		const partnerBrokerId = this.data.partner.id;

		if (this.data.mode === 'link') {
			this.legacyOrgService
				.assignBrokerToOrganization(partnerBrokerId, selectedOrgId)
				.pipe(takeUntil(this.destroy$))
				.subscribe({
					error: (error) => {
						this.snackBarService.showSnack(
							error?.error?.message || 'Falha ao vincular organização.',
							'error',
						);
					},
					next: () => {
						const name = this.data?.partner?.name || '';
						const message =
							name && String(name).trim() !== ''
								? `Organização vinculada à corretora "${name}" com sucesso.`
								: 'Organização vinculada à corretora com sucesso.';
						this.snackBarService.showSnack(message, 'success');
						this.dialogRef.close(true);
					},
				});
		} else {
			this.legacyOrgService
				.unassignBrokerFromOrganization(selectedOrgId)
				.pipe(takeUntil(this.destroy$))
				.subscribe({
					error: (error) => {
						this.snackBarService.showSnack(
							error?.error?.message || 'Falha ao desvincular organização.',
							'error',
						);
					},
					next: () => {
						const name = this.data?.partner?.name || '';
						const message =
							name && String(name).trim() !== ''
								? `Organização desvinculada da corretora "${name}" com sucesso.`
								: 'Organização desvinculada da corretora com sucesso.';
						this.snackBarService.showSnack(message, 'success');
						this.dialogRef.close(true);
					},
				});
		}
	}

	trackByOrgId(
		index: number,
		org: { id: number | string } | null,
	): number | string | null {
		return org ? org.id : null;
	}

	private searchText(org: OrganizationDto): string {
		const nameSecondary = (org.name_secondary ?? '').toLowerCase();
		const id = String(org.id ?? '').toLowerCase();

		return `${nameSecondary} ${id}`.trim();
	}

	private setupFilterStream(): void {
		this.selectedOrgId = null;

		this.filteredOrganizations$ = this.organizationControl.valueChanges.pipe(
			startWith(this.organizationControl.value ?? ''),
			map((value) =>
				typeof value === 'string' ? value : (value?.name_secondary ?? ''),
			),
			map((term) => term.toLowerCase().trim()),
			map((term) => {
				if (!term) return this.orgCache;

				return this.orgCache.filter((o) => this.searchText(o).includes(term));
			}),
		);
	}
}
